import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { SidebarSectionType } from 'src/components/sidebarSection/SidebarSection';
import { TreeComponent } from 'src/components/tree/Tree';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import { ROUTE } from 'src/constants/routes';
import { useNewAddResourceMapping } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddResourceMapping';
import { useMappingsTree } from 'src/containers/ProjectSidebar/hooks/useMappingsTree';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectSelector } from 'src/selectors/project';
import { projectResourceSelectItems } from 'src/selectors/projectResource';
import * as copy from 'src/templates/copy';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

export const useMappingsSection = (
  filter: string
): SidebarSectionType | null => {
  const { projectId } = useRouterParams<'PROJECT_MAPPING'>();
  const isMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const resourceItems = useReduxSelector(projectResourceSelectItems(projectId));
  const hasResourceItems = Boolean(resourceItems.length);

  const defaultPortalConnection = useDefaultPortalConnection();
  const isAutoMappingMultipleTablesSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isAutoMappingMultipleTablesSupported',
      defaultPortalConnection
    )
  );
  const addDisabled = !isAutoMappingMultipleTablesSupported;

  const portalFeatures = usePortalFeatures();
  const project = useReduxSelector(projectSelector(projectId));
  const useVoiceboxSuggestions =
    Boolean(useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions) &&
    !project?.disableVoiceboxSuggestions;

  const handleAddMapping = useNewAddResourceMapping();
  const onAdd = React.useMemo(
    () =>
      hasResourceItems || useVoiceboxSuggestions ? handleAddMapping : undefined,
    [handleAddMapping, hasResourceItems, useVoiceboxSuggestions]
  );

  const tree = useMappingsTree(filter);

  return React.useMemo(() => {
    if (!isMappingRoute) {
      return null;
    }

    return {
      id: ProjectSidebarTileId.MAPPINGS,
      addDisabled,
      addDisabledTooltip:
        copy.components.project.mappings.resourceSuggestionsDisabled,
      allCount: tree.allCount,
      items: (
        <TreeComponent
          nodes={tree.filteredTree}
          onNodeClick={tree.onNodeClick}
        />
      ),
      itemsClassname: styles.projectSidebarMappingsItems,
      itemsCount: tree.itemsCount,
      title: copy.components.project.mappings.title,
      onAdd,
    };
  }, [addDisabled, isMappingRoute, tree, onAdd]);
};
